/**
 * Справочники значений `/catalog/enums` (§9.2).
 *
 * Слева шесть наборов, справа значения выбранного. Три решения спеки, которые
 * здесь видно руками:
 *  · хранимый код после создания неизменяем — он уже лежит в данных и в истории,
 *    поэтому правится только подпись, и интерфейс об этом говорит;
 *  · значения не удаляются, а отключаются: история не должна ссылаться в пустоту;
 *  · порядок значений = сортировка «по приоритету» в таблице и порядок строк
 *    в поповерах правки, поэтому он правится здесь же.
 * «Используется у N» считается по загруженному срезу брендов — цифра честная
 * для данных брендов и не требует отдельного запроса.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Brand, EnumValue } from '@shared/types.ts'
import {
  Badge, Button, Checkbox, EmptyState, FieldRow, IconArrowDown, IconArrowUp, IconPlus, IconProhibited,
  Input, Modal, Skeleton, IconButton, Popover, IconCheck, IconChevronDown,
  CHIP_COLORS, chipColor, chipColorLabel,
  type ChipColor, type MenuItem,
} from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { AdminError, AdminPage, RowActions } from '../admin/AdminPage.js'
import { adminTexts } from '../admin/texts.admin.js'
import { errorText, loadEnumSets, type EnumSetView } from '../admin/admin-api.js'
import './EnumsScreen.css'

const t = adminTexts.enums

/** Где в срезе брендов живут значения каждого набора (§9.2, колонка «Используется у»). */
function usageBySet(brands: Brand[]): Map<string, Map<string, number>> {
  const out = new Map<string, Map<string, number>>()
  const bump = (setCode: string, value: string | null) => {
    if (value === null) return
    const bucket = out.get(setCode) ?? new Map<string, number>()
    bucket.set(value, (bucket.get(value) ?? 0) + 1)
    out.set(setCode, bucket)
  }

  for (const brand of brands) {
    for (const channel of Object.values(brand.channels)) {
      if (!channel) continue
      bump('tier', channel.tier)
      bump('visibility', channel.visibility)
      bump('stock_display', channel.stockDisplay)
      bump('oos', channel.oos)
      bump('seo_priority', channel.seoPriority)
      bump('context_priority', channel.contextPriority)
    }
    for (const category of brand.categories) {
      for (const channel of Object.values(category.channels)) {
        if (!channel) continue
        bump('tier', channel.tier)
      }
    }
  }
  return out
}

export function EnumsScreen() {
  const toast = useApp((s) => s.toast)
  const brands = useApp((s) => s.brands)

  const [sets, setSets] = useState<EnumSetView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  /* Набор можно открыть ссылкой: ?set=seo_priority. Так из выпадающего
     списка в таблице попадают сразу в нужный справочник. */
  const [params] = useSearchParams()
  const wantedSet = params.get('set')
  const [activeCode, setActiveCode] = useState<string | null>(wantedSet)
  const [busy, setBusy] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [newValue, setNewValue] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  const [renameId, setRenameId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameRef = useRef<HTMLInputElement | null>(null)
  const renameDone = useRef(true)

  /** Открытая палитра цвета: id значения, у которого сейчас выбирают цвет. */
  const [colorFor, setColorFor] = useState<number | null>(null)
  const colorAnchors = useRef<Record<number, HTMLButtonElement | null>>({})

  /** Новый справочник. */
  const [createSetOpen, setСreateSetOpen] = useState(false)
  const [setName, setSetName] = useState('')
  const [setCode, setSetCode] = useState('')
  const [setError, setSetError] = useState<string | null>(null)

  /** Пояснение к значению: что этот тег значит. Правится прямо в строке. */
  const [hintId, setHintId] = useState<number | null>(null)
  const [hintValue, setHintValue] = useState('')
  const hintDone = useRef(true)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const items = await loadEnumSets()
      setSets(items)
      setActiveCode((current) => current ?? items[0]?.code ?? null)
    } catch (err) {
      setLoadError(errorText(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (renameId !== null) renameRef.current?.focus()
  }, [renameId])

  const usage = useMemo(() => usageBySet(brands), [brands])

  const activeSet = useMemo(
    () => sets?.find((s) => s.code === activeCode) ?? null,
    [sets, activeCode],
  )

  async function createValue() {
    if (!activeSet) return
    const label = newLabel.trim()
    const value = newValue.trim()
    if (label.length === 0 || value.length === 0) {
      setCreateError(adminTexts.common.required)
      return
    }
    setBusy(true)
    try {
      await api.createEnumValue(activeSet.code, { label, value })
      setCreateOpen(false)
      setNewLabel('')
      setNewValue('')
      setCreateError(null)
      toast('success', t.toastCreated(label))
      await load()
    } catch (err) {
      setCreateError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function finishRename(setCode: string, value: EnumValue, apply: boolean) {
    if (renameDone.current) return
    renameDone.current = true
    setRenameId(null)
    const label = renameValue.trim()
    if (!apply || label.length === 0 || label === value.label) return
    try {
      await api.updateEnumValue(setCode, value.id, { label })
      toast('success', t.toastRenamed(label))
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  async function createSet(): Promise<void> {
    const name = setName.trim()
    const code = setCode.trim()
    if (name === '') { setSetError(adminTexts.enums.setName); return }
    if (!/^[a-z][a-z0-9_]*$/.test(code)) { setSetError(adminTexts.enums.setCodeHint); return }
    setBusy(true)
    try {
      await api.createEnumSet({ code, name })
      setСreateSetOpen(false)
      toast('success', t.toastSetCreated(name))
      await load()
      setActiveCode(code)
    } catch (err) {
      setSetError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  function startHint(value: EnumValue): void {
    hintDone.current = false
    setHintId(value.id)
    setHintValue(value.hint ?? '')
  }

  async function finishHint(setCode: string, value: EnumValue, apply: boolean) {
    if (hintDone.current) return
    hintDone.current = true
    setHintId(null)
    const hint = hintValue.trim()
    if (!apply || hint === (value.hint ?? '')) return
    try {
      await api.updateEnumValue(setCode, value.id, { hint: hint === '' ? null : hint })
      toast('success', t.toastHint(value.label))
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  async function pickColor(setCode: string, value: EnumValue, color: ChipColor) {
    setColorFor(null)
    if (chipColor(value.color) === color) return
    try {
      await api.updateEnumValue(setCode, value.id, { color })
      toast('success', t.toastColor(value.label, chipColorLabel(color)))
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  async function toggleActive(setCode: string, value: EnumValue, isActive: boolean) {
    try {
      await api.updateEnumValue(setCode, value.id, { isActive })
      toast('info', t.toastActive(value.label, isActive))
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  /**
   * Перестановка значения на одну позицию. Отдельной ручки «порядок» в клиенте
   * API нет, поэтому переставляем значение в списке и проставляем порядок
   * подряд (1…N) — правятся только те значения, у которых номер изменился.
   * Сквозная нумерация важнее парного обмена: в базе порядок может быть нулевым
   * у всего набора, и обмен местами тогда ничего бы не изменил.
   */
  async function move(set: EnumSetView, index: number, direction: -1 | 1) {
    const ordered = [...set.values]
    const current = ordered[index]
    const neighbour = ordered[index + direction]
    if (!current || !neighbour) return
    ordered[index] = neighbour
    ordered[index + direction] = current

    setBusy(true)
    try {
      for (const [position, value] of ordered.entries()) {
        const sortOrder = position + 1
        if (value.sortOrder === sortOrder) continue
        await api.updateEnumValue(set.code, value.id, { sortOrder })
      }
      toast('info', t.toastOrder)
      await load()
    } catch (err) {
      toast('error', errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const rowMenu: MenuItem[] = [{ id: 'rename', label: adminTexts.common.rename }]

  /** Enter в поле окна создания = главное действие: Modal его не перехватывает. */
  const submitOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    void createValue()
  }

  return (
    <AdminPage
      wide
      title={t.title}
      subtitle={adminTexts.common.immediate}
      /* Кнопка добавления уехала под таблицу: значение заводят, дочитав
         список до конца, а не до него. */
    >
      {loadError ? <AdminError message={loadError} onRetry={() => { void load() }} /> : null}

      {!loadError && sets === null ? (
        <div className="adm-table-wrap">
          <Skeleton rows={6} columns={[200, 140, 80, 120, 100]} label={adminTexts.common.loading} />
        </div>
      ) : null}

      {!loadError && sets !== null ? (
        <div className="enums">
          <nav className="enums__sets" aria-label={t.setsLabel}>
            <ul>
              {sets.map((set) => (
                <li key={set.code}>
                  <button
                    type="button"
                    className={set.code === activeCode ? 'enums__set enums__set--on' : 'enums__set'}
                    aria-current={set.code === activeCode ? 'true' : undefined}
                    onClick={() => setActiveCode(set.code)}
                  >
                    <span className="enums__set-name">{set.name}</span>
                    <span className="enums__set-count t-meta">{set.values.length}</span>
                  </button>
                </li>
              ))}
            </ul>
            {/* Новый справочник заводится здесь же: колонке со своим набором
                значений раньше требовалась миграция и разработчик. */}
            <button
              type="button"
              className="enums__add-set"
              onClick={() => { setSetError(null); setSetName(''); setSetCode(''); setСreateSetOpen(true) }}
            >
              {t.addSet}
            </button>
          </nav>

          <section className="enums__values" aria-label={activeSet?.name ?? t.title}>
            {activeSet ? (
              <>
                <p className="adm__sub t-meta">{t.orderNote}</p>
                {activeSet.hint ? <p className="enums__hint t-meta">{activeSet.hint}</p> : null}

                {activeSet.isLocked ? (
                  <p className="adm-note adm-note--muted">
                    <IconProhibited size={16} className="adm-note__icon" />
                    <span>{t.lockedNote}</span>
                  </p>
                ) : null}

                <p className="adm-note">
                  <span>{`${t.valueImmutable} ${t.deleteNote} ${t.colorNote}`}</span>
                </p>

                {activeSet.values.length === 0 ? (
                  <EmptyState
                    title={t.emptyTitle}
                    text={t.emptyText}
                    actions={
                      <Button variant="secondary" onClick={() => setCreateOpen(true)}>{t.add}</Button>
                    }
                  />
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table enum-table">
                      <colgroup>
                        <col className="enum-col--label" />
                        <col className="enum-col--value" />
                        <col className="enum-col--color" />
                        <col className="enum-col--hint" />
                        <col className="enum-col--usage" />
                        <col className="enum-col--active" />
                        <col className="enum-col--order" />
                        <col className="enum-col--actions" />
                        <col />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col" className="t-col">{t.colLabel}</th>
                          <th scope="col" className="t-col">{t.colValue}</th>
                          <th scope="col" className="t-col">{t.colColor}</th>
                          <th scope="col" className="t-col">{t.colHint}</th>
                          <th scope="col" className="t-col adm-cell--num">{t.colUsage}</th>
                          <th scope="col" className="t-col">{t.colActive}</th>
                          <th scope="col" className="t-col">{t.colOrder}</th>
                          <th scope="col" className="t-col">
                            <span className="visually-hidden">{adminTexts.common.rowMenu}</span>
                          </th>
                          <th scope="col" />
                        </tr>
                      </thead>
                      <tbody>
                        {activeSet.values.map((value, index) => {
                          const used = usage.get(activeSet.code)?.get(value.value) ?? 0
                          return (
                            <tr key={value.id} className={value.isActive ? undefined : 'adm-row--archived'}>
                              <td>
                                {renameId === value.id ? (
                                  <div className="adm-inline">
                                    <Input
                                      ref={renameRef}
                                      size="cell"
                                      value={renameValue}
                                      aria-label={t.labelField}
                                      onChange={(event) => setRenameValue(event.target.value)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault()
                                          void finishRename(activeSet.code, value, true)
                                        }
                                        if (event.key === 'Escape') {
                                          event.preventDefault()
                                          void finishRename(activeSet.code, value, false)
                                        }
                                      }}
                                      onBlur={() => { void finishRename(activeSet.code, value, true) }}
                                    />
                                  </div>
                                ) : (
                                  <div className="adm-name">
                                    <span className="adm-name__text">{value.label}</span>
                                    {value.isActive ? null : (
                                      <Badge variant="neutral">{adminTexts.common.statusArchived}</Badge>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="adm-cell--code">{value.value}</td>
                              {/* Цвет чипа: образец-кнопка, по нажатию — палитра.
                                  Выбор из восьми имён, а не произвольный код:
                                  произвольный легко даёт нечитаемую пару и
                                  разъезжается в тёмной теме. */}
                              <td>
                                <button
                                  type="button"
                                  className="enum-color"
                                  ref={(node) => { colorAnchors.current[value.id] = node }}
                                  aria-haspopup="menu"
                                  aria-expanded={colorFor === value.id}
                                  aria-label={t.colorPick(value.label)}
                                  onClick={() => setColorFor(colorFor === value.id ? null : value.id)}
                                >
                                  <span
                                    className="enum-chip enum-color__sample"
                                    data-color={chipColor(value.color)}
                                    aria-hidden="true"
                                  >
                                    {value.label}
                                  </span>
                                  <IconChevronDown size={16} />
                                </button>
                                <Popover
                                  open={colorFor === value.id}
                                  anchor={colorAnchors.current[value.id] ?? null}
                                  onClose={() => setColorFor(null)}
                                  role="menu"
                                  placement="bottom-start"
                                  minWidth={190}
                                >
                                  <div className="enum-palette">
                                    {CHIP_COLORS.map((item) => (
                                      <button
                                        key={item.value}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={chipColor(value.color) === item.value}
                                        className="enum-palette__item"
                                        onClick={() => void pickColor(activeSet.code, value, item.value)}
                                      >
                                        <span className="chip-swatch" data-color={item.value} aria-hidden="true" />
                                        <span className="enum-palette__name">{item.label}</span>
                                        {chipColor(value.color) === item.value
                                          ? <IconCheck size={16} />
                                          : null}
                                      </button>
                                    ))}
                                  </div>
                                </Popover>
                              </td>
                              {/* Пояснение: что значит тег. Видно там, где
                                  значение выбирают, — в выпадающем списке. */}
                              <td>
                                {hintId === value.id ? (
                                  <Input
                                    size="cell"
                                    autoFocus
                                    value={hintValue}
                                    aria-label={t.colHint}
                                    placeholder={t.hintPlaceholder}
                                    onChange={(event) => setHintValue(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault()
                                        void finishHint(activeSet.code, value, true)
                                      }
                                      if (event.key === 'Escape') {
                                        event.preventDefault()
                                        void finishHint(activeSet.code, value, false)
                                      }
                                    }}
                                    onBlur={() => { void finishHint(activeSet.code, value, true) }}
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    className={value.hint ? 'enum-hint' : 'enum-hint enum-hint--empty'}
                                    onClick={() => startHint(value)}
                                  >
                                    {value.hint ?? t.hintAdd}
                                  </button>
                                )}
                              </td>
                              <td className="adm-cell--num">
                                {used > 0 ? used : <span className="enum-zero">0</span>}
                              </td>
                              <td>
                                <Checkbox
                                  checked={value.isActive}
                                  aria-label={`${t.colActive}: ${value.label}`}
                                  onChange={(checked) => { void toggleActive(activeSet.code, value, checked) }}
                                />
                              </td>
                              <td>
                                <div className="adm-cell__row">
                                  <IconButton
                                    label={`${adminTexts.common.moveUp}: ${value.label}`}
                                    icon={<IconArrowUp size={16} />}
                                    disabled={index === 0 || busy}
                                    onClick={() => { void move(activeSet, index, -1) }}
                                  />
                                  <IconButton
                                    label={`${adminTexts.common.moveDown}: ${value.label}`}
                                    icon={<IconArrowDown size={16} />}
                                    disabled={index === activeSet.values.length - 1 || busy}
                                    onClick={() => { void move(activeSet, index, 1) }}
                                  />
                                </div>
                              </td>
                              <td className="adm-cell--actions">
                                <RowActions
                                  label={`${adminTexts.common.rowMenu}: ${value.label}`}
                                  items={rowMenu}
                                  onSelect={(id) => {
                                    if (id !== 'rename') return
                                    setRenameValue(value.label)
                                    renameDone.current = false
                                    setRenameId(value.id)
                                  }}
                                />
                              </td>
                              <td />
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Добавление — под таблицей, справа: значение заводят,
                    дочитав список до конца, а не до него. */}
                <div className="enums__add">
                  <Button
                    variant="secondary"
                    iconBefore={<IconPlus size={16} />}
                    onClick={() => {
                      setCreateError(null)
                      setNewLabel('')
                      setNewValue('')
                      setCreateOpen(true)
                    }}
                  >
                    {t.add}
                  </Button>
                </div>
              </>
            ) : null}
          </section>
        </div>
      ) : null}

      <Modal
        open={createSetOpen}
        onClose={() => setСreateSetOpen(false)}
        title={t.createSetTitle}
        size="s"
        onConfirm={() => { void createSet() }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setСreateSetOpen(false)}>
              {adminTexts.common.cancel}
            </Button>
            <Button variant="primary" loading={busy} onClick={() => { void createSet() }}>
              {t.createSetSubmit}
            </Button>
          </>
        }
      >
        <div className="adm-modal-stack">
          <p className="adm-note adm-note--muted">{t.createSetIntro}</p>
          <FieldRow label={t.setName} htmlFor="enum-set-name" consequence={t.setNameHint}>
            <Input
              id="enum-set-name"
              autoFocus
              value={setName}
              placeholder={t.setNamePlaceholder}
              onChange={(event) => {
                setSetName(event.target.value)
                setSetError(null)
                /* Код предлагаем сами: руками его почти всегда набирают так же. */
                if (setCode === '') return
              }}
            />
          </FieldRow>
          <FieldRow label={t.setCode} htmlFor="enum-set-code" consequence={t.setCodeHint}>
            <Input
              id="enum-set-code"
              value={setCode}
              placeholder={t.setCodePlaceholder}
              error={setError ?? undefined}
              onChange={(event) => { setSetCode(event.target.value); setSetError(null) }}
            />
          </FieldRow>
        </div>
      </Modal>

      <Modal
        open={createOpen && activeSet !== null}
        onClose={() => setCreateOpen(false)}
        title={activeSet ? t.createTitle(activeSet.name) : t.add}
        size="s"
        onConfirm={() => { void createValue() }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {adminTexts.common.cancel}
            </Button>
            <Button variant="primary" loading={busy} onClick={() => { void createValue() }}>
              {t.createSubmit}
            </Button>
          </>
        }
      >
        {/* У полей появились подписи: два безымянных ввода подряд читались
            как ошибка вёрстки — было непонятно, чем они отличаются и что
            куда попадёт. */}
        <div className="adm-modal-stack">
          <p className="adm-note adm-note--muted">{t.createIntro}</p>

          <FieldRow label={t.labelField} htmlFor="enum-new-label" consequence={t.labelHint}>
            <Input
              id="enum-new-label"
              autoFocus
              value={newLabel}
              placeholder={t.labelPlaceholder}
              onChange={(event) => {
                setNewLabel(event.target.value)
                setCreateError(null)
              }}
              onKeyDown={submitOnEnter}
            />
          </FieldRow>

          <FieldRow label={t.valueField} htmlFor="enum-new-value" consequence={t.valueHint}>
            <Input
              id="enum-new-value"
              value={newValue}
              placeholder={t.valuePlaceholder}
              error={createError ?? undefined}
              onChange={(event) => {
                setNewValue(event.target.value)
                setCreateError(null)
              }}
              onKeyDown={submitOnEnter}
            />
          </FieldRow>
        </div>
      </Modal>
    </AdminPage>
  )
}
